// client/src/app/Header.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Link, useLocation } from "react-router-dom";
import {
  useDisclosure,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { useEmbedMode } from "../providers/EmbedModeProvider";
import { useFeatureFlags } from "../providers/FeatureFlagsProvider";
import { useBrand } from "../providers/BrandProvider";


import logoUrl from "../../assets/logo.png";

// ---------- Config ----------
const MAX_WIDTH = 1600;
const LOGO_HEIGHT = 52;
const TOP_ROW_HEIGHT = 56;
const LOGO_LEFT_PADDING = 12;
const LOGO_TOP = Math.round((TOP_ROW_HEIGHT - LOGO_HEIGHT) / 2);

const isResPath = (p) =>
  ["/reservations", "/overview", "/tables", "/month", "/print", "/list"].some((r) => p.startsWith(r));

// ---------- Styles ----------
const headerWrap = {
  position: "sticky",
  top: 0,
  zIndex: 50,
  background: "var(--bg, #fff)",
  borderBottom: "1px solid var(--border, #e5e7eb)",
};

const container = {
  maxWidth: MAX_WIDTH,
  margin: "0 auto",
  padding: "0 16px",
};

const topRowOuter = {
  position: "relative",
  height: TOP_ROW_HEIGHT,
};

const topRowInner = {
  height: TOP_ROW_HEIGHT,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const logoAbsolute = {
  position: "absolute",
  left: LOGO_LEFT_PADDING,
  top: LOGO_TOP,
  display: "inline-flex",
};

const logoImg = {
  height: LOGO_HEIGHT,
  width: "auto",
  display: "block",
};

const mainNavWrap = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const topLink = ({ isActive }) => ({
  padding: "8px 12px",
  textDecoration: "none",
  borderRadius: 8,
  fontWeight: 600,
  color: isActive ? "#fff" : "var(--fg, #111827)",
  background: isActive ? "var(--brand, #3b6fd4)" : "transparent",
  transition: "background .15s ease, color .15s ease",
});

const subRow = {
  borderTop: "1px solid var(--border, #e5e7eb)",
};

const subRowInner = {
  width: "100%",
  padding: "4px 0",
};

const subNavOuter = {
  width: "100%",
  overflowX: "auto",
  scrollbarWidth: "none",
};

const subNav = {
  width: "max-content",
  margin: "0 auto",
  display: "flex",
  gap: 16,
  alignItems: "center",
  padding: "0 16px",
  whiteSpace: "nowrap",
};

const subLink = ({ isActive }) => ({
  fontSize: 13,
  lineHeight: 1,
  textDecoration: "none",
  fontWeight: 600,
  color: isActive ? "var(--fg, #111827)" : "var(--muted, #6b7280)",
  padding: "0 0 6px 0",
  borderBottom: `2px solid ${isActive ? "var(--brand, #3b6fd4)" : "transparent"}`,
  transition: "color .15s ease, border-color .15s ease",
});

// ---------- Component ----------

const NAV_KEYS = [
  { to: "/home", key: "nav.home" },
  { to: "/reservations", key: "nav.reservations" },
  { to: "/staff", key: "nav.tasks" },
  { to: "/settings", key: "nav.settings" },
];

const themeToggleBtn = (active, primaryColor) => ({
  fontSize: 12,
  padding: "4px 8px",
  border: "1px solid #ddd",
  borderRadius: 4,
  cursor: "pointer",
  background: active ? (primaryColor || "#3b6fd4") : "transparent",
  color: active ? "#fff" : "#666",
  lineHeight: 1.4,
  whiteSpace: "nowrap",
});

export default function Header() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isIframe } = useEmbedMode();
  const { has } = useFeatureFlags();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { brandConfig, themeMode, toggleTheme, hasBrand } = useBrand();
  const showSub = isResPath(pathname);
  const showBrandLogo = themeMode === "brand" && brandConfig.logoUrl;
  const mainNavItems = NAV_KEYS.map((item) => ({ to: item.to, label: t(item.key) }));

  const subItems = [
    { to: "/reservations", label: "Overview" },
    { to: "/list", label: "List" },
    has("tables") && { to: "/tables", label: "Table View" },
    has("month") && { to: "/month", label: "Month" },
    { to: "/print", label: "Print" },
  ].filter(Boolean);

  return (
    <header style={headerWrap}>
      <div style={topRowOuter}>
        <Link to="/reservations" aria-label="Home" style={logoAbsolute}>
          <img
            src={showBrandLogo ? brandConfig.logoUrl : logoUrl}
            alt={showBrandLogo ? "Restaurant logo" : "YeboGuest"}
            style={logoImg}
          />
        </Link>


        <div style={container}>
          <div style={topRowInner}>
            {/* Desktop nav — hidden below md */}
            <nav style={mainNavWrap} className="header-desktop-nav" aria-label="Main navigation">
              {mainNavItems.map((item) => (
                <NavLink key={item.to} to={item.to} style={topLink}>{item.label}</NavLink>
              ))}
            </nav>

            {/* Mobile hamburger — visible below md */}
            <IconButton
              className="header-mobile-hamburger"
              icon={<HamburgerIcon />}
              onClick={onOpen}
              aria-label="Open menu"
              variant="ghost"
              size="md"
            />
          </div>
        </div>

        {/* Brand theme toggle — right side */}
        {hasBrand && (
          <button
            onClick={toggleTheme}
            style={{
              ...themeToggleBtn(themeMode === "brand", brandConfig.primaryColor),
              position: "absolute",
              right: 16,
              top: Math.round((TOP_ROW_HEIGHT - 28) / 2),
            }}
            aria-label={themeMode === "brand" ? "Switch to default theme" : "Switch to brand theme"}
          >
            {themeMode === "brand" ? "Brand" : "Default"}
          </button>
        )}

        {/* Mobile drawer */}
        <Drawer isOpen={isOpen} onClose={onClose} placement="left">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerBody pt={12}>
              <nav style={{ display: "flex", flexDirection: "column", gap: 8 }} aria-label="Mobile navigation">
                {mainNavItems.map((item) => (
                  <NavLink key={item.to} to={item.to} style={topLink} onClick={onClose}>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </div>

      {showSub && subItems.length > 1 && (
        <div style={subRow}>
          <div style={subRowInner}>
            <div style={subNavOuter} aria-label="Reservations sub navigation">
              <div style={subNav}>
                {subItems.map((i) => (
                  <NavLink key={i.to} to={i.to} style={subLink}>
                    {i.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
